import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  getPendingConnections,
  acceptConnection,
  rejectConnection,
  PendingConnectionDto,
} from '../../services/user-connections.service';
import { getDefaultPermissions } from '../../services/access-control.service';
import { ConnectionAccessItem } from '../../types/access-control.types';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { toastService } from '../../services/toast.service';

interface PermissionsPreviewProps {
  permissions: ConnectionAccessItem[] | null;
  moduleLabels: Record<string, string>;
  permissionLabels: Record<string, string>;
}

const PermissionsPreview: React.FC<PermissionsPreviewProps> = ({ permissions, moduleLabels, permissionLabels }) => {
  if (!permissions) {
    return (
      <Typography variant="caption" color="text.secondary">
        Стандартні права доступу
      </Typography>
    );
  }

  const active = permissions.filter(p => p.permissions.length > 0);
  if (active.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Без прав доступу
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {active.map(p => (
        <Chip
          key={p.code}
          icon={<VpnKeyIcon sx={{ fontSize: '12px !important' }} />}
          label={`${moduleLabels[p.code] ?? p.code}: ${p.permissions.map(r => permissionLabels[r] ?? r).join(', ')}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: 11, height: 20 }}
        />
      ))}
    </Box>
  );
};

const ConnectionRequestsWidget: React.FC = () => {
  const [requests, setRequests] = useState<PendingConnectionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [moduleLabels, setModuleLabels] = useState<Record<string, string>>({});
  const [permissionLabels, setPermissionLabels] = useState<Record<string, string>>({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, schema] = await Promise.all([getPendingConnections(), getDefaultPermissions()]);
      setRequests(data);
      setModuleLabels(Object.fromEntries(schema.map(m => [m.code, m.label])));
      setPermissionLabels(Object.fromEntries(
        [...new Map(schema.flatMap(m => m.permissions).map(p => [p.code, p.label])).entries()]
      ));
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося завантажити заявки.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleAccept = async (id: number) => {
    setProcessingId(id);
    try {
      await acceptConnection(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      toastService.success('Заявку прийнято');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося прийняти заявку.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: number) => {
    setProcessingId(id);
    try {
      await rejectConnection(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      toastService.info('Заявку відхилено');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося відхилити заявку.');
    } finally {
      setProcessingId(null);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <Box display="flex" justifyContent="center" sx={{ py: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
      return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
    }

    if (!requests.length) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, color: 'text.secondary' }}>
          <PeopleAltIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography variant="body2">Нових заявок немає</Typography>
        </Box>
      );
    }

    return (
      <List disablePadding>
        {requests.map((req, index) => {
          const fullName = [req.name, req.surname].filter(Boolean).join(' ');
          const isProcessing = processingId === req.id;

          return (
            <React.Fragment key={req.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="center"
                sx={{ px: 0 }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Прийняти">
                      <span>
                        <IconButton
                          color="success"
                          onClick={() => handleAccept(req.id)}
                          disabled={isProcessing}
                          size="small"
                        >
                          <CheckIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Відхилити">
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => handleReject(req.id)}
                          disabled={isProcessing}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemAvatar>
                  <UserAvatar path={req.avatar} name={req.name} sx={{ width: 40, height: 40 }} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="body1" fontWeight={500}>{fullName}</Typography>}
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Typography variant="body2" color="text.secondary" component="span">
                        {new Date(req.createdAt).toLocaleDateString('uk-UA')}
                      </Typography>
                      <PermissionsPreview permissions={req.customPermissions} moduleLabels={moduleLabels} permissionLabels={permissionLabels} />
                    </Box>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box>
      <Typography variant="h5">
        Заявки на співпрацю
        {requests.length > 0 && (
          <Typography component="span" variant="body2" color="primary" sx={{ ml: 1 }}>
            ({requests.length})
          </Typography>
        )}
      </Typography>
      {renderContent()}
    </Box>
  );
};

export default ConnectionRequestsWidget;
